import { Injectable } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { DjangoApiService } from '../django-api.service';
import { PdfService } from './pdf.service';
import { ChartDataService } from '../plotly/chart-data.service';
import * as Plotly from 'plotly.js-dist-min';
import { PlotlyService } from '../plotly/plotly.service';

@Injectable({
  providedIn: 'root'
})
export class PdfForPageService {
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  constructor(
    private _apiService: DjangoApiService,
    private _pdfService: PdfService,
    private _plotlyService: PlotlyService,
    private _chartDataService: ChartDataService,
  ) {}

  public initializeHeader(productTitle: string, reportTitle: string, reportDate: string): void {
    this._pdfService.initializeHeader(productTitle, reportTitle, reportDate);
  }

  /**
   * Generiert ein PDF als Base64‑String für den angegebenen Report und die übergebene Sprache.
   */
  public generatePdfForPageBinary(report: any, language: string, product?: any): Promise<string> {
    return new Promise((resolve, reject) => {
      // Wir gehen davon aus, dass der Caller bereits this._pdfService.reset() aufgerufen hat!
      this._pdfService.initializeCurrentLanguage(language);
      if (report.translations[language].comment) {
        this._pdfService.initializeComment(report.translations[language].comment);
      }
      // Header initialisieren, falls Produktdaten vorhanden sind:
      if (product && product.translations && product.translations[language]) {
        this._pdfService.initializeHeader(
          product.translations[language].title,
          report.translations[language].title,
          report.date
        );
      }
      if(product){
        this._pdfService.initializeProduct(product);
      }

      // Hole die Seitendaten anhand von report.page_template
      this._apiService.fetchPage(report.page_template).subscribe({
        next: (pageData: any) => {
          this._apiService.fetchSectionList(pageData.id, report.date).subscribe({
            next: (sectionList: any) => {
              this._pdfService.initializeDataSource(sectionList);
              let currentIndex = 0;
              const processNextSection = () => {
                if (currentIndex >= sectionList.length) {
                  // Nachdem alle Abschnitte verarbeitet wurden, baue das docDefinition
                  const docDefinition = this._pdfService.buildDocDefinition();
                  this._pdfService.generatePdfBinary(docDefinition)
                    .then((data: string) => resolve(data))
                    .catch((err: any) => reject(err));
                  return;
                }
                const section = sectionList[currentIndex];
                currentIndex++;
                this._apiService.fetchDataSourceList(section.id, pageData.product_id).subscribe({
                  next: customDataList => {
                    this._apiService.fetchAllocationListProcessed(section.id, pageData.product_id).subscribe({
                      next: allocationDataList => {
                        const combinedCustomAllocation = [...customDataList, ...allocationDataList].filter(
                          (data: any) => data.section.includes(section.id)
                        );
                        if (combinedCustomAllocation.length > 0) {
                          const dataItem = combinedCustomAllocation[0];
                          this.createPdfChartForCustomData(dataItem, section.id, section).then(() => {
                            this.fetchAndProcessStatistics(section.id, pageData, report, product).then(() => {
                              processNextSection();
                            });
                          }).catch((err: any) => {
                            console.error('Error creating custom chart for PDF:', err);
                            processNextSection();
                          });
                        } else {
                          this.fetchAndProcessStatistics(section.id, pageData, report, product).then(() => {
                            processNextSection();
                          });
                        }
                      },
                      error: (err: any) => {
                        console.error('Error fetching allocation data:', err);
                        processNextSection();
                      }
                    });
                  },
                  error: (err: any) => {
                    console.error('Error fetching custom data list:', err);
                    processNextSection();
                  }
                });
              };
              processNextSection();
            },
            error: (err: any) => {
              reject(err);
            }
          });
        },
        error: (err: any) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Hauptmethode: generatePdfForPage – lädt alles headless, generiert alle Charts
   */
  public generatePdfForPage(targetPageSlug: string, productSlug: string, dateString?: string, language?: string, comment?: string, report?: any) {
    alert('Generating PDF for page: ' + targetPageSlug);
    this._pdfService.reset();
    this._pdfService.initializeCurrentLanguage(language ?? 'en');
    if (comment) {
      this._pdfService.initializeComment(comment);
    }


    console.log('generatePdfForPage: Aufruf gestartet mit', targetPageSlug, productSlug);
    this._apiService.fetchPage(targetPageSlug, productSlug).subscribe({
      next: pageData => {
        console.log('fetchPage returned:', pageData);
        // Falls Produktdaten fehlen, explizit abrufen
        this._apiService.fetchProductDetail(productSlug).subscribe({
          next: (productData: any) => {
            console.log('fetchProductDetail returned:', productData);
            pageData.product = productData;
            console.log('pageData.product:', pageData.product);
            this.processSections(pageData, dateString ?? '', report);
          },
          error: (err: any) => {
            console.error('Error fetching product data:', err);
            this.processSections(pageData, dateString ?? '', report);
          }
        });
      },
      error: (err: any) => {
        console.error('Error fetching page data:', err);
      }
    });

  }

  private processSections(pageData: any, dateString: string, report: any, product?: any): void {
    this._apiService.fetchSectionList(pageData.id, dateString).subscribe({
      next: sectionList => {
        this._pdfService.initializeDataSource(sectionList);
        let currentIndex = 0;
        const processNextSection = () => {
          if (currentIndex >= sectionList.length) {
            this._pdfService.generatePdf();
            return;
          }
          const section = sectionList[currentIndex];
          currentIndex++;
          this._apiService.fetchDataSourceList(section.id, pageData.product_id).subscribe({
            next: customDataList => {
              this._apiService.fetchAllocationListProcessed(section.id, pageData.product_id).subscribe({
                next: allocationDataList => {
                  const combinedCustomAllocation = [...customDataList, ...allocationDataList].filter(
                    (data: any) => data.section.includes(section.id)
                  );
                  if (combinedCustomAllocation.length > 0) {
                    const dataItem = combinedCustomAllocation[0];
                    this.createPdfChartForCustomData(dataItem, section.id, section).then(() => {
                      this.fetchAndProcessStatistics(section.id, pageData, report, product).then(() => {
                        processNextSection();
                      });
                    });
                  } else {
                    this.fetchAndProcessStatistics(section.id, pageData, report, product).then(() => {
                      processNextSection();
                    });
                  }
                },
                error: (err: any) => {
                  console.error('Error fetching allocation data:', err);
                  processNextSection();
                }
              });
            },
            error: (err: any) => {
              console.error('Error fetching custom data list:', err);
              processNextSection();
            }
          });
        };
        processNextSection();
      },
      error: (err: any) => {
        console.error('Error loading section list:', err);
      }
    });
  }

  private fetchAndProcessStatistics(sectionId: number, pageData: any, report: any, product?: any): Promise<void> {
    return new Promise<void>((resolve) => {
      this._apiService.fetchStatisticList(sectionId).subscribe({
        next: statisticData => {
          if (statisticData && statisticData.length > 0) {
            const stat = statisticData[0];
            this.createPdfChartForStatistic(stat, pageData.product_id, sectionId, pageData, report, product)
              .then(() => {
                resolve();
              })
              .catch((err: any) => {
                console.error('Error creating PDF chart for statistic:', err);
                resolve();
              });
          } else {
            resolve();
          }
        },
        error: (err: any) => {
          console.error('Error fetching statistic list:', err);
          resolve();
        }
      });
    });
  }

  private async createPdfChartForCustomData(dataItem: any, section: any, fullSection: any) {
    try {
      const chartMeta = fullSection.statistics && fullSection.statistics[0] ? fullSection.statistics[0] : null;
      const desiredChartType = chartMeta ? chartMeta.chart : 'bar';
      const translation = dataItem.translations['en'];
      if (!translation || !translation.data_json?.rows) {
        console.warn('Kein gültiges data_json in den Übersetzungen gefunden:', dataItem);
        return;
      }
      let plotlyType: Plotly.PlotType;
      let traces: Partial<Plotly.PlotData>[];
      if (desiredChartType === 'pie' || desiredChartType === 'donut') {
        traces = [
          this._plotlyService.generateTracesForPieChartPdfForPages(
            translation,
            translation.data_json.rows,
            true,
            desiredChartType
          )
        ];
        plotlyType = 'pie';
      } else if (desiredChartType === 'bar') {
        traces = this._plotlyService.generateTracesFor2DCharts(
          'bar',
          translation.data_json.rows,
          true,
          { data: translation.data_json },
          'bar'
        );
        plotlyType = 'bar';
      } else if (desiredChartType === 'line') {
        traces = this._plotlyService.generateTracesFor2DCharts(
          'scatter',
          translation.data_json.rows,
          true,
          { data: translation.data_json },
          'line'
        );
        traces.forEach(t => t.mode = 'lines');
        plotlyType = 'scatter';
      } else if (desiredChartType === 'horizontalBar') {
        const chartData = [{
          data: translation.data_json.rows,
          name: dataItem.name,
          isBenchmark: false
        }];
        traces = this._plotlyService.createHorizontalBarTraces(chartData);
        plotlyType = 'bar';
      } else {
        traces = this._plotlyService.generateTracesFor2DCharts(
          'bar',
          translation.data_json.rows,
          true,
          { data: translation.data_json },
          'bar'
        );
        plotlyType = 'bar';
      }
      const layout = this._plotlyService.generateLayout(plotlyType, traces, desiredChartType);
      const config = { responsive: true, displayModeBar: false };
      const chartDiv = this.createHiddenDiv();
      const uniqueId = 'hidden-' + section.id + '-' + new Date().getTime();
      chartDiv.id = uniqueId;
      let chartHeight = 300;
      if (desiredChartType === 'horizontalBar') {
        chartHeight = 150;
      }
      chartDiv.style.height = chartHeight + 'px';
      document.body.appendChild(chartDiv);
      await this._plotlyService.renderChart(uniqueId, traces, layout, config);
      const svgString = await Plotly.toImage(uniqueId, {
        format: 'svg',
        width: 800,
        height: chartHeight
      });
      const cleanedSvg = decodeURIComponent(svgString.replace('data:image/svg+xml,', '')).trim();
      document.body.removeChild(chartDiv);
      this._pdfService.appendChart({ id: section, image: cleanedSvg });
    } catch (err) {
      console.error('Error creating custom chart for PDF:', err);
    }
  }

  private async createPdfChartForStatistic(stat: any, productId: number, sectionId: number, pageData: any, report: any, product?: any): Promise<void> {
    try {
      const statisticChoice = stat.statistics?.[0]?.statistic_choice || 'performance';
      const interval = stat.frequency || 'monthly';
      const benchmarkIds: number[] = [];
      const startDate = '';
      const endDate = report.date;
      const chartResponse = await firstValueFrom(
        this._apiService.fetchChartData(
          productId,
          benchmarkIds,
          startDate,
          endDate,
          interval,
          statisticChoice
        )
      );
      if (stat.chart === 'table') {
        const tableData = chartResponse;
        if (!tableData) {
          return;
        }
        const tableContent = this.createTableContent(tableData);
        this._pdfService.initializeStatisticTable({ id: sectionId, data: tableContent });
        return;
      }
      const normalizedData = this._chartDataService.processChartData(chartResponse, pageData.product);
      const sampleData = normalizedData[0]?.data;
      const xRange = sampleData ? this.getInitialXRangeForPdf(sampleData) : undefined;
      let traces: Partial<Plotly.PlotData>[];
      let layout: Partial<Plotly.Layout>;
      const config = { responsive: true, displayModeBar: false };
      if (stat.chart === 'line_bar_combined') {
        traces = this._plotlyService.createCombinedLineBarTraces(normalizedData, product.phases);
        layout = this._plotlyService.getBaseLayoutCombined(false, product.phases, xRange);
      } else {
        const result = this._plotlyService.createTraces(
          normalizedData,
          stat.chart,
          product.phases
        );
        traces = result.traces;
        layout = this._plotlyService.getBaseLayout(false, product.phases, xRange);
        console.log("PDF: xRange:", xRange);
        console.log("PDF: Product phases:", product.phases);
        console.log("PDF: Result verticalShapes:", result.verticalShapes);
        console.log("PDF: Result verticalAnnotations:", result.verticalAnnotations);
        if (!layout.shapes) {
          layout.shapes = [];
        }
        if (!layout.annotations) {
          layout.annotations = [];
        }
        layout.shapes = layout.shapes.concat(result.verticalShapes);
        layout.annotations = layout.annotations.concat(result.verticalAnnotations);
        console.log("PDF: Final layout shapes:", layout.shapes);
        console.log("PDF: Final layout annotations:", layout.annotations);
      }
      const chartDiv = this.createHiddenDiv();
      const uniqueId = 'hidden-stat-' + sectionId + '-' + new Date().getTime();
      chartDiv.id = uniqueId;
      document.body.appendChild(chartDiv);
      await this._plotlyService.renderChart(uniqueId, traces, layout, config);
      const svgString = await Plotly.toImage(uniqueId, { format: 'svg', width: 800, height: 300 });
      const cleanedSvg = decodeURIComponent(svgString.replace('data:image/svg+xml,', '')).trim();
      document.body.removeChild(chartDiv);
      this._pdfService.appendChart({ id: sectionId, image: cleanedSvg });
    } catch (err) {
      console.error('Error creating statistic chart for PDF:', err);
    }
  }

  private createTableContent(tableData: any): any {
    if (!tableData || !tableData.header || !tableData.rows) {
      console.warn('Ungültiges Tabellenformat:', tableData);
      return null;
    }
    return {
      content: [tableData.header, ...tableData.rows],
      withHeadings: true
    };
  }

  private getInitialXRangeForPdf(data: any[]): [string, string] | undefined {
    if (!data || data.length === 0) return undefined;
    const dates = data.map((point: any) => new Date(point.date));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
    return [minDate.toISOString().split('T')[0], maxDate.toISOString().split('T')[0]];
  }

  private createHiddenDiv(): HTMLDivElement {
    const div = document.createElement('div');
    div.style.width = '800px';
    div.style.height = '300px';
    div.style.position = 'absolute';
    div.style.left = '-9999px';
    return div;
  }
}
